import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Loader2, UsersRound, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import { useRefData } from "@/context/RefDataContext";

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className="text-sm text-zinc-200">{value || <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRefData();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/teams/${id}`);
      setTeam(data);
    } catch (e) {
      toast.error("Team not found");
      navigate("/teams-users");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team…
      </div>
    );
  }
  if (!team) return null;

  const lead = (ref.users || []).find((u) => u.value === team.team_lead_id);
  const memberOptions = (ref.users || []).filter((u) => (team.members || []).includes(u.value));

  return (
    <div className="space-y-8" data-testid="team-detail-page">
      <Link to="/teams-users" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back to Teams &amp; Users
      </Link>

      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <UsersRound className="w-3.5 h-3.5" /> Team
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }} data-testid="team-detail-name">
            {team.name}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-zinc-500">
            {team.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{team.email}</span>}
            <span>Members: <span className="text-zinc-300">{memberOptions.length}</span></span>
          </div>
        </div>
      </header>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Team Lead &amp; Membership
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <Row label="Team Lead" value={lead?.label} />
          <Row label="Members" value={memberOptions.length ? memberOptions.map((u) => u.label).join(", ") : ""} />
        </div>
      </section>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="text-[11px] text-zinc-500 italic leading-relaxed">
          Module Access and Approval Permissions are now configured per user.
          Open any user&apos;s detail page (Teams &amp; Users → Users tab → click a user)
          to grant or revoke modules and approval queues.
        </div>
      </section>
    </div>
  );
}
